import {Box, Group, Paper, Stack, Title} from '@mantine/core';
import {FC, useCallback, useMemo} from 'react';
import {SubmitHandler} from 'react-hook-form';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {useAsync} from 'react-use';
import {FieldTypes} from '../../api/generated/enums';
import {JobsService} from '../../api/generated/JobsService';
import {AsyncStateContainer} from '../../components/async-state-container';
import {createForm} from '../../forms';
import {useNotification} from '../../hooks/use-notifications';
import {ROUTES} from '../../routes';
import {JobFieldsSchema, jobFieldsSchema} from '../schemas';
import classes from './job-evaluation.module.css';
import {useJobStore} from '../job-store';

const JobFieldsForm = createForm<JobFieldsSchema>();

type JobRepairServicePerformedFormFieldsProps = {
  isJobReadonly?: boolean;
  defaultValues: JobFieldsSchema;
  onSubmit: SubmitHandler<JobFieldsSchema>;
};

const JobRepairServicePerformedFormFields: FC<
  JobRepairServicePerformedFormFieldsProps
> = ({isJobReadonly, defaultValues, onSubmit}) => (
  <>
    <JobFieldsForm
      defaultValues={defaultValues}
      schema={jobFieldsSchema}
      onSubmit={onSubmit}
    >
      <Group gap={0} className={classes.jobFieldsContainer}>
        <JobFieldsForm.FieldArray name={'jobFields'}>
          {({fields}) =>
            fields.map((item, index) => {
              return (
                <Box w='100%' m={8} key={index}>
                  {item.type === FieldTypes.Checkbox ? (
                    <JobFieldsForm.Checkbox
                      name={`jobFields.${index}.value`}
                      disabled={isJobReadonly}
                      label={item.name}
                    />
                  ) : item.type === FieldTypes.Date ? (
                    <JobFieldsForm.DateTimePicker
                      name={`jobFields.${index}.value`}
                      disabled={isJobReadonly}
                      label={item.name}
                    />
                  ) : item.type === FieldTypes.Number ? (
                    <JobFieldsForm.NumberInput
                      name={`jobFields.${index}.value`}
                      disabled={isJobReadonly}
                      label={item.name}
                    />
                  ) : item.type === FieldTypes.Text ? (
                    <JobFieldsForm.TextInput
                      name={`jobFields.${index}.value`}
                      disabled={isJobReadonly}
                      label={item.name}
                    />
                  ) : null}
                </Box>
              );
            })
          }
        </JobFieldsForm.FieldArray>
      </Group>
      <Group mt={16}>
        <JobFieldsForm.Submit disabled={isJobReadonly} ml='auto'>
          Save Fields
        </JobFieldsForm.Submit>
      </Group>
    </JobFieldsForm>
  </>
);

export const JobRepairServicePerformed: FC = () => {
  const {id} = useTypedParams(ROUTES.ADMIN.JOBS.DETAIL);
  const notifications = useNotification();

  const {isJobReadonly} = useJobStore((state) => ({
    isJobReadonly: state.isJobReadonly,
  }));

  const fetchJobFields = useAsync(async () => {
    const {data} = await JobsService.getJobFields({
      id,
    });
    return data;
  }, [id]);

  const onSubmit = useCallback<SubmitHandler<JobFieldsSchema>>(
    async (value) => {
      const response = await JobsService.updateRepairServicePerformed({
        id,
        body: value,
      });
      if (response.hasErrors) {
        return response;
      }

      notifications.success('Repair/Service Performed Updated');
    },
    [id, notifications],
  );

  const defaultValues = useMemo(
    () => ({
      jobFields:
        fetchJobFields.value?.map((item) => {
          item.value =
            item.value && item.type === FieldTypes.Date
              ? new Date(item.value)
              : item.value ?? undefined;
          return item;
        }) ?? [],
    }),
    [fetchJobFields],
  );

  return (
    <>
      <Title fz='sm' className={classes.title}>
        Repair/Service Performed
      </Title>
      <Paper
        withBorder
        radius='0 0 6px 6px'
        shadow='sm'
        className={classes.formPaper}
      >
        <Stack p={16} pt={0}>
          <AsyncStateContainer {...fetchJobFields}>
            {defaultValues && (
              <JobRepairServicePerformedFormFields
                defaultValues={defaultValues as JobFieldsSchema}
                isJobReadonly={isJobReadonly}
                onSubmit={onSubmit}
              />
            )}
          </AsyncStateContainer>
        </Stack>
      </Paper>
    </>
  );
};
